import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BiSolidLock } from 'react-icons/bi';
import { FaRegHandshake } from 'react-icons/fa6';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router';

import { getServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { Divider } from '@/components';
import { ActionsButton, type ActionMenuItem } from '@/components/ActionsButton';
import { Avatar } from '@/components/Avatar';
import { ButtonSmall } from '@/components/Button';
import { CommentsSection } from '@/components/CommentsSection';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { EventHeader } from '@/components/EventCard/EventHeader';
import { EventTime } from '@/components/EventCard/EventTime';
import {
  CAROUSEL_SLIDE_HEIGHT,
  CAROUSEL_SLIDE_WIDTH,
  Carousel,
  getCarouselImageSrc,
  getCarouselSlideSrc,
  type CarouselImage,
} from '@/components/ImageCarousel';
import Modal from '@/components/Modal/Modal';
import OutOfWaterBadge from '@/components/OutOfWaterBadge/OutOfWaterBadge';
import ParticipantBadge from '@/components/ParticipantBadge/ParticipantBadge';
import { PriorityBadge } from '@/components/TaskBadges/TaskBadges';
import { TaskChecklist, useChecklistAutosave } from '@/components/TaskChecklist';
import { isTaskDueSoon, isTaskExpired } from '@/components/TaskExpiringLabel/taskExpiration';
import { TaskExpiredLabel } from '@/components/TaskExpiringLabel/TaskExpiredLabel';
import { TaskExpiringLabel } from '@/components/TaskExpiringLabel/TaskExpiringLabel';
import { TaskFiles } from '@/components/TaskFiles';
import TaskStatusesBadge from '@/components/TaskStatusesBadge';
import { useCommentsSection } from '@/hooks/useCommentsSection';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { PersonsMap } from '@/hooks/usePersonsMap';
import { useRoles } from '@/hooks/useRoles';
import { useServiceProvidersMap } from '@/hooks/useServiceProvidersMap';
import InvoiceModal from '@/pages/Expenses/components/modals/InvoiceModal';
import QuoteModal from '@/pages/Expenses/components/modals/QuoteModal';
import { SERVICE_PROVIDER_ASSIGN_TO_SEARCH_KEY } from '@/pages/ServiceProviders/SingleServiceProvider';
import { PERMISSIONS } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import { useAuth } from '@/store/auth/auth.store';
import type { BidRoom } from '@/store/chats/chat.dto';
import { useChats } from '@/store/chats/chats.store.ts';
import type { EventAttachment } from '@/store/events/events.dto';
import type { Invoice } from '@/store/expenses-store/invoices/invoices.dto';
import type { Quote } from '@/store/expenses-store/quotes/quote.dto';
import { TODO_DONE_STATUSES, type TaskDocument, type TaskStatus } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import { UserRole } from '@/utils/roles';
import { toast } from '@/utils/toast';
import StartIcon from '@assets/stars-ai.svg?react';

import { isOutOfWater } from '../calendarUtils';

import s from './EventModal.module.scss';

/** What a task's file list is, as far as this modal is concerned: which files, in order. */
const documentIds = (documents: TaskDocument[]) => documents.map(({ id }) => id).join(',');

interface EventModalProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  event: any;
  personsMap: PersonsMap;
  bidResponse?: boolean;
  quotes?: Quote[] | null;
  invoices?: Invoice[] | null;
  /**
   * The task's service requests, when the caller has already loaded them — so the
   * modal opens with the section it will keep rather than growing one. Left out, the
   * modal asks for them itself.
   */
  bids?: BidRoom[] | null;
  /**
   * The caller has already loaded the task's comments into the store, so the comments
   * section shows them rather than asking for the same thread a second time.
   */
  commentsPreloaded?: boolean;
  onEdit?: (id: string, item: any) => void;
  onDelete?: (id: string, item: any) => void;
  vendors?: { name: string; comment: string | null; status: string | null }[];
  /**
   * Which statuses the status chip offers. Left out, it offers the owner's full set —
   * except on a task carrying equipment, which is a maintenance job or a service request
   * and so gets To do / Done on its own. The maintenance surfaces pass
   * TODO_DONE_STATUSES outright, so a job opened from the schedule cannot be moved into
   * a state the schedule does not model.
   */
  statusOptions?: readonly TaskStatus[];
}

const EventModal = ({
  isModalOpen,
  handleCloseModal,
  event,
  personsMap,
  quotes,
  invoices,
  bids: preloadedBids,
  commentsPreloaded = false,
  onEdit,
  onDelete,
  statusOptions,
  vendors,
}: EventModalProps) => {
  const navigate = useNavigate();
  const { primaryRole, userRoles, hasAnyRole } = useRoles();
  const isServiceProvider = userRoles?.includes(UserRole.SERVICE_PROVIDER);
  const { updateTaskStatus, tasksList, attachTaskDocuments, deleteTaskDocument } = useTasks();
  const { can } = usePermissions();
  const myId = useAuth((state) => state.session?.id);

  const extendedProps = useMemo(() => event.extendedProps ?? event, [event]);
  const eventId = extendedProps.id ?? event.id;
  const eventType = extendedProps.type ?? 'general';
  const isPrivateTask = 'visibility' in extendedProps && extendedProps.visibility === 'private';
  const startDate = extendedProps.time_slot?.start
    ? new Date(extendedProps.time_slot.start)
    : event.start;
  const endDate = extendedProps.time_slot?.end ? new Date(extendedProps.time_slot.end) : event.end;
  const participants = useMemo(
    () => extendedProps.participants ?? [],
    [extendedProps.participants],
  );
  const commentsCount = extendedProps.comments_count ?? 0;
  const liveStatus = tasksList?.items.find((t) => t.id === eventId)?.status;
  const status = liveStatus ?? extendedProps.status ?? null;
  const is_ai_generated = extendedProps.is_ai_generated;

  /**
   * A task carrying equipment is a maintenance job or a service request, and those are
   * only ever outstanding or finished — In Progress and On Review describe delegated
   * task workflow neither one models. An explicit `statusOptions` still wins, and the
   * cut is the owner's view only: a provider works in_progress / on_approval, which is
   * disjoint from To do / Done, so trimming their list would leave them nothing to pick.
   */
  const statusChoices =
    statusOptions ??
    (extendedProps.equipment && !isServiceProvider ? TODO_DONE_STATUSES : undefined);

  /**
   * Same equipment signal as above: a maintenance job's urgency is carried by its
   * schedule — Overdue / Due soon — so a priority pill would be a second, competing
   * claim about the same thing. Plain tasks and events keep it.
   */
  const isMaintenanceJob = Boolean(extendedProps.equipment);

  const serviceProviderIds = useMemo(
    () =>
      participants.filter((p: any) => p.type === 'service_provider').map((p: any) => p.person_id),
    [participants],
  );
  const { map: serviceProvidersMap } = useServiceProvidersMap(serviceProviderIds);

  const {
    displayMessages,
    isHidden,
    messageInput,
    setMessageInput,
    handleToggleComments,
    handleSendMessage,
    handleKeyDown,
    hasComments,
    displayCount,
    isSending,
    refetchMessages,
  } = useCommentsSection({
    entityId: eventId,
    personsMap,
    commentsCount,
    initiallyExpanded: true,
    preloaded: commentsPreloaded,
  });

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ name: string; src: string } | null>(
    null,
  );

  const attachments = useMemo<CarouselImage[]>(
    () =>
      (extendedProps.attachments ?? []).map(
        (a: EventAttachment) =>
          ({
            type: 'attachment',
            id: a.id,
            src: a.link,
            thumb: imageVariantLink(a, CAROUSEL_SLIDE_WIDTH, CAROUSEL_SLIDE_HEIGHT) ?? undefined,
            name: a.name,
          }) as const,
      ),
    [extendedProps.attachments],
  );

  // Only tasks carry files — an event's own attachments are the photo carousel
  // above. Events have a status too, so what tells the two apart is `visibility`:
  // a task is private or public, an event is neither.
  const isTask = 'visibility' in extendedProps || 'documents' in extendedProps;
  const canEditTask = can(PERMISSIONS.activity.Tasks);
  // ExternalTasks ticks only a task the user, or the service they act for, is on: the service's
  // own rule. The list shows them the vessel's other tasks too.
  const isOnTask = [...participants, extendedProps.supervisor, extendedProps.responsible].some(
    (p) => !!p?.person_id && [myId, getServiceId()].includes(p.person_id),
  );
  const canTickTask = canEditTask || (can(PERMISSIONS.activity.ExternalTasks) && isOnTask);
  // Guests see the task, not the crew's working steps behind it.
  const isGuest = hasAnyRole([UserRole.GUEST]) && !canEditTask;
  const incomingDocuments: TaskDocument[] = useMemo(
    () => extendedProps.documents ?? [],
    [extendedProps.documents],
  );
  const [documents, setDocuments] = useState<TaskDocument[]>(incomingDocuments);
  const syncedDocumentIds = useRef(documentIds(incomingDocuments));
  const [isFilesBusy, setIsFilesBusy] = useState(false);
  const [removingDocumentId, setRemovingDocumentId] = useState<string | null>(null);

  // The modal is handed whichever copy of the task the surface behind it holds, so
  // a switch to another task — or a refresh that changes its files — brings the
  // right list with it. Compared by id rather than by identity: the same files
  // arrive in a new array on any render, and adopting that would undo an upload
  // this modal just made.
  useEffect(() => {
    const ids = documentIds(incomingDocuments);
    if (ids === syncedDocumentIds.current) return;
    syncedDocumentIds.current = ids;
    setDocuments(incomingDocuments);
  }, [incomingDocuments]);

  const handleAttachFiles = async (files: File[]) => {
    setIsFilesBusy(true);
    try {
      setDocuments(await attachTaskDocuments(eventId, files));
      toast.success(files.length === 1 ? 'File attached' : 'Files attached');
    } catch (err: unknown) {
      console.error(err);
      toast.error(
        files.length === 1
          ? 'Couldn’t attach the file. Please try again.'
          : 'Couldn’t attach the files. Please try again.',
      );
    } finally {
      setIsFilesBusy(false);
    }
  };

  // Held here for the same reason the files are: a change shows at once and saves behind it.
  const [checklist, handleChecklistChange] = useChecklistAutosave(eventId, extendedProps.checklist);

  const handleRemoveFile = async (documentId: string) => {
    setRemovingDocumentId(documentId);
    try {
      await deleteTaskDocument(eventId, documentId);
      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      toast.success('File removed');
    } catch (err: unknown) {
      console.error(err);
      toast.error('Couldn’t remove the file. Please try again.');
    } finally {
      setRemovingDocumentId(null);
    }
  };

  const isMobile = useIsMobile();
  const isOutOfWaterEvent = isOutOfWater(extendedProps);

  // Events carry no cost, and an unpriced task carries null — both leave the row out
  // rather than showing a $0 nobody entered.
  const costLabel =
    typeof extendedProps.cost === 'number' && Number.isFinite(extendedProps.cost)
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(extendedProps.cost)
      : null;

  const actionItems = useMemo<ActionMenuItem[]>(() => {
    const items: ActionMenuItem[] = [];
    if (onEdit) {
      items.push({
        label: 'Edit',
        onClick: () => {
          handleCloseModal();
          onEdit(eventId, extendedProps);
        },
      });
    }
    if (onDelete) {
      items.push({
        label: 'Delete',
        onClick: () => {
          handleCloseModal();
          onDelete(eventId, extendedProps);
        },
        variant: 'danger',
      });
    }
    return items;
  }, [eventId, extendedProps, onEdit, onDelete, handleCloseModal]);

  /**
   * Held here and tagged with the task they belong to, not read from the chats store:
   * the store keeps one `bids` slice for the whole app, so the next task opened showed
   * the last one's requests until its own arrived.
   */
  const getBids = useChats((state) => state.getBids);
  const [fetchedBids, setFetchedBids] = useState<{ taskId: string; rooms: BidRoom[] } | null>(null);

  useEffect(() => {
    if (preloadedBids !== undefined) return;
    const taskId = extendedProps.id;
    let cancelled = false;
    getBids(taskId)
      .then((rooms) => {
        if (!cancelled) setFetchedBids({ taskId, rooms });
      })
      .catch((error: unknown) => {
        console.error('Failed to load the task requests:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [getBids, extendedProps.id, preloadedBids]);

  const bids =
    preloadedBids !== undefined
      ? preloadedBids
      : fetchedBids && fetchedBids.taskId === extendedProps.id
        ? fetchedBids.rooms
        : null;

  const additionalServiceHandler = () => {
    navigate(
      `/service-providers?${new URLSearchParams({ [SERVICE_PROVIDER_ASSIGN_TO_SEARCH_KEY]: eventId })}`,
    );
  };

  return (
    <Modal open={isModalOpen} onClose={handleCloseModal}>
      <div className={clsx(s.eventModalContent, isMobile && s.mobile)}>
        <div className={s.headerActions}>
          {/* The labels wrap as a group; the menu beside them does not, so it keeps the
              row's first line however many lines the chips take. */}
          <div className={s.headerChips}>
            {isTaskDueSoon(event.extendedProps) && (
              <TaskExpiringLabel extended task={event.extendedProps} />
            )}
            {isTaskExpired(event.extendedProps) && (
              <TaskExpiredLabel extended task={event.extendedProps} />
            )}
            {is_ai_generated && (
              <div className={clsx(s.ai, 'body-small')}>
                <StartIcon />
                {!isMobile && ' AI Task'}
              </div>
            )}
            {/* Only a task carries a priority, and the badge renders nothing for an
                event's missing value — so the row needs no `isTask` gate of its own. */}
            {!isMaintenanceJob && (
              <PriorityBadge priority={extendedProps.priority} className={s.priority} />
            )}
            {status && (
              <TaskStatusesBadge
                status={status}
                isServiceProvider={isServiceProvider}
                options={statusChoices}
                onChange={(status) =>
                  updateTaskStatus({ id: eventId, serviceId: getServiceId(), status })
                }
                className={s.status}
              />
            )}
          </div>
          {actionItems.length > 0 && (
            <div className={s.headerMenu}>
              <ActionsButton items={actionItems} ariaLabel="Event actions" />
            </div>
          )}
          <div style={{ width: 8, height: 32 }}></div>
        </div>
        <div className={s.headerRow}>
          <EventHeader
            type={eventType}
            title={event.title}
            fontSize="14px"
            icon={isPrivateTask ? <BiSolidLock color="#FFB800" /> : undefined}
          />
        </div>

        <div className={s.centeredContent}>
          {startDate && endDate && (
            <EventTime
              start={extendedProps.time_slot?.start ?? startDate.toISOString()}
              end={extendedProps.time_slot?.end ?? endDate.toISOString()}
            />
          )}
          {isOutOfWaterEvent && <OutOfWaterBadge />}
          {costLabel && (
            <p className={s.cost}>
              Est. cost: <b>{costLabel}</b>
            </p>
          )}
        </div>

        {extendedProps.description && (
          <div className={clsx(s.description, 'md-content')}>
            <ReactMarkdown>{extendedProps.description}</ReactMarkdown>
          </div>
        )}

        {isTask && !isGuest && (checklist.length > 0 || canEditTask) && (
          <TaskChecklist
            items={checklist}
            onChange={canTickTask ? handleChecklistChange : undefined}
            tickOnly={!canEditTask}
          />
        )}

        {attachments.length > 0 && (
          <>
            <p style={{ fontSize: 14 }}>Photos ({attachments.length})</p>
            <Carousel>
              <Carousel.Prev />
              <Carousel.Track>
                {attachments.map((img, i) => (
                  <Carousel.Slide
                    key={i}
                    src={getCarouselSlideSrc(img)}
                    onClick={() =>
                      setPreviewAttachment({
                        name: img.type === 'attachment' ? img.name : '',
                        src: getCarouselImageSrc(img),
                      })
                    }
                  />
                ))}
              </Carousel.Track>
              <Carousel.Next />
            </Carousel>
          </>
        )}

        {isTask && (
          <TaskFiles
            documents={documents}
            // Without the Tasks permission the section is what is attached and no
            // more — the files still open and download from here.
            onAttach={canEditTask ? (files) => void handleAttachFiles(files) : undefined}
            onRemoveDocument={
              canEditTask ? (documentId) => void handleRemoveFile(documentId) : undefined
            }
            isBusy={isFilesBusy}
            removingId={removingDocumentId}
          />
        )}

        {participants.length > 0 && (
          <>
            <div className={s.participants}>
              {participants.map((participant: any) => {
                if (participant.type === 'service_provider') {
                  const sp = serviceProvidersMap.get(participant.person_id);
                  if (!sp?.name) return null;
                  return (
                    <ParticipantBadge
                      key={participant.person_id}
                      participantId={participant.person_id}
                      firstName={sp.name}
                      lastName=""
                      participantRole={participant.role}
                      avatarUrl={imageVariantLink(sp.logo, 24)}
                    />
                  );
                }
                const person = personsMap.get(participant.person_id);
                if (!person?.person_info.first_name) return null;
                return (
                  <ParticipantBadge
                    key={participant.person_id}
                    participantId={participant.person_id}
                    firstName={person.person_info.first_name}
                    lastName={person.person_info.last_name}
                    participantRole={participant.role}
                    avatarUrl={imageVariantLink(person.avatar, 24)}
                  />
                );
              })}
            </div>
            <Divider />
          </>
        )}

        {vendors && vendors.length > 0 && (
          <>
            <section className={s.vendors}>
              <p>Vendors engaged ({vendors.length})</p>
              <ul>
                {vendors.map((vendor) => (
                  <li key={vendor.name}>
                    <b>{vendor.name}</b>
                    {vendor.status === 'engaged' && <span className={s.vendorTag}>engaged</span>}
                    {vendor.comment && <span>{vendor.comment}</span>}
                  </li>
                ))}
              </ul>
            </section>
            <Divider />
          </>
        )}

        {status && bids && bids.length > 0 && (
          <>
            <section className={s.bidResponse}>
              <p>Requests ({bids.length})</p>
              {bids.map(({ id, last_message, name, creator_id, users, room_unread_messages }) => {
                const serviceProvider = users?.find((u) => u.id !== creator_id);
                const displayName = serviceProvider?.display_name || name || 'Service provider';
                return (
                  <article key={id} onClick={() => navigate(`/maintenance/${id}`)}>
                    <div className={s.logoWrapper}>
                      <Avatar
                        src={imageVariantLink(serviceProvider?.avatar, 32)}
                        firstName={displayName}
                        alt={displayName}
                        size={32}
                      />
                    </div>
                    <section>
                      <p className={s.bidName}>{displayName}</p>
                      <span>
                        {last_message !== null
                          ? last_message?.text === ''
                            ? 'No messages yet'
                            : last_message?.text
                          : 'No messages yet'}
                      </span>
                    </section>
                    {!!room_unread_messages && (
                      <div className={s.number}>{room_unread_messages}</div>
                    )}
                  </article>
                );
              })}
            </section>
            <Divider />
          </>
        )}

        {invoices && invoices?.length > 0 && (
          <>
            <p>Invoices ({invoices?.length})</p>
            <section className={s.quotes}>
              {invoices?.map((invoice) => {
                const { id, company_name, invoice_number, date, amount } = invoice;
                return (
                  <article
                    key={id}
                    className={s.invoice}
                    onClick={() => setSelectedInvoice(invoice)}
                  >
                    <div className={s.quoteRow}>
                      <p className="body-medium">Invoice #{invoice_number}</p>
                      <span className="body-medium">
                        {new Date(date as string).toLocaleDateString()}
                      </span>
                    </div>
                    <div className={s.quoteRow}>
                      <p className={clsx(s.invoiceCompany, 'body-medium')}>{company_name}</p>
                      <span className={clsx('body-medium', s.invoiceAmount)}>
                        ${Number(amount.number).toFixed(2)}
                      </span>
                    </div>
                  </article>
                );
              })}
            </section>
            <Divider />
          </>
        )}

        {quotes && quotes?.length > 0 && (
          <>
            <p>Quotes ({quotes.length})</p>
            <section className={s.quotes}>
              {quotes.map((quote) => {
                const { id, quote_number, date, equipments, amount } = quote;
                return (
                  <article key={id} className={s.quote} onClick={() => setSelectedQuote(quote)}>
                    <div className={s.quoteRow}>
                      <p className="body-medium">Quote #{quote_number}</p>
                      <span className="body-medium">
                        {date ? new Date(date).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className={s.quoteRow}>
                      <p className="body-small">{equipments[0]?.name}</p>
                      <span className={clsx('body-medium', s.quoteAmount)}>
                        ${Number(amount.number).toFixed(2)}
                      </span>
                    </div>
                  </article>
                );
              })}
            </section>
            <Divider />
          </>
        )}
        {!isPrivateTask && (
          <div className={s.commentsWrapper}>
            <CommentsSection
              displayMessages={displayMessages}
              isHidden={isHidden}
              messageInput={messageInput}
              hasComments={hasComments}
              displayCount={displayCount}
              onToggleComments={handleToggleComments}
              onMessageInputChange={setMessageInput}
              onSendMessage={handleSendMessage}
              onKeyDown={handleKeyDown}
              isSending={isSending}
              scrollable
              onRefetchMessages={refetchMessages}
            />
          </div>
        )}

        {/* status property is available only in tasks, and this button should only be shown for tasks, so this check is necessary, and working like it now */}
        {status &&
          primaryRole !== UserRole.SERVICE_PROVIDER &&
          primaryRole !== UserRole.CREW_MEMBER && (
            <ButtonSmall onClick={additionalServiceHandler} className={s.addService}>
              <div className={s.addServiceContent}>
                <FaRegHandshake style={{ rotate: '-45deg' }} fontSize={20} />
                <span>Request Additional Service</span>
              </div>
            </ButtonSmall>
          )}
      </div>

      {previewAttachment && (
        <DocumentPreviewModal
          document={{
            contentType: 'image/jpeg',
            name: previewAttachment.name,
            storageLink: previewAttachment.src,
          }}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
      <InvoiceModal
        open={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        invoice={selectedInvoice}
        isExist
      />
      <QuoteModal
        open={!!selectedQuote}
        onClose={() => setSelectedQuote(null)}
        quote={selectedQuote}
        isExist
      />
    </Modal>
  );
};

export default EventModal;
